import { motion } from 'framer-motion'
import useStore from '../store'
import { CONFIG } from '../config/config'

export default function AnalysisPage() {
  const goals      = useStore(s => s.goals)
  const phases     = useStore(s => s.phases)
  const reviews    = useStore(s => s.reviews)
  const has        = useStore(s => s.has)
  const accuracy   = useStore(s => s.estimationAccuracy())
  const consistency = useStore(s => s.consistencyScore())

  const allGoals     = goals.filter(g => g.status !== 'draft')
  const completed    = goals.filter(g => g.status === 'completed')
  const donePhases   = phases.filter(p => p.status === 'completed')

  // Category map
  const catMap = {}
  allGoals.filter(g => g.category).forEach(g => {
    catMap[g.category] = (catMap[g.category] ?? 0) + 1
  })
  const catTotal = Object.values(catMap).reduce((s, c) => s + c, 0)

  // Effort distribution
  const effortMap = { 1: 0, 2: 0, 3: 0 }
  allGoals.forEach(g => { effortMap[g.effort_level] = (effortMap[g.effort_level] ?? 0) + 1 })

  return (
    <motion.div
      className="h-full overflow-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    >
      <div className="max-w-xl mx-auto px-5 py-5 flex flex-col gap-4">

        <h1 className="font-mono text-sm text-m-text">Analysis</h1>

        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { l: 'Total goals',   v: allGoals.length },
            { l: 'Completed',     v: completed.length,  accent: true },
            { l: 'Phases done',   v: donePhases.length },
            { l: 'Reviews',       v: reviews.length },
          ].map(({ l, v, accent }) => (
            <div key={l} className="card p-3">
              <p className="label">{l}</p>
              <p className={`font-mono text-2xl mt-1 ${accent ? 'text-m-accent' : 'text-m-text'}`}>{v}</p>
            </div>
          ))}
        </div>

        {/* Estimation accuracy */}
        {accuracy ? (
          <div className="card p-4">
            <p className="label mb-2">Estimation accuracy</p>
            <p className={`font-mono text-4xl mb-1 ${
              accuracy.ratio > 1.2 ? 'text-m-warn' : accuracy.ratio < 0.8 ? 'text-m-accent' : 'text-m-text'
            }`}>
              {(accuracy.ratio * 100).toFixed(0)}%
            </p>
            <p className="font-mono text-xs text-m-faint">
              of estimated time used across {accuracy.count} completed phases.
              {accuracy.ratio > 1.2 && ' Consistent underestimation — add buffer.'}
              {accuracy.ratio < 0.8 && ' Consistent overestimation — tighten estimates.'}
              {accuracy.ratio >= 0.8 && accuracy.ratio <= 1.2 && ' Calibration is solid.'}
            </p>
          </div>
        ) : (
          <div className="card p-4 opacity-40">
            <p className="label mb-1">Estimation accuracy</p>
            <p className="font-mono text-xs text-m-faint">Complete a phase to unlock.</p>
          </div>
        )}

        {/* Execution consistency */}
        {reviews.length >= CONFIG.MIN_REVIEWS_FOR_TREND ? (
          <div className="card p-4">
            <p className="label mb-2">Execution consistency</p>
            <p className={`font-mono text-4xl mb-1 ${
              (consistency ?? 0) >= 80 ? 'text-m-accent' :
              (consistency ?? 0) >= 60 ? 'text-m-warn'   : 'text-m-danger'
            }`}>
              {consistency !== null ? `${consistency}%` : '—'}
            </p>
            <p className="font-mono text-xs text-m-faint">
              of review weeks where you hit ≥80% of planned hours.
            </p>
          </div>
        ) : (
          <div className="card p-4 opacity-40">
            <p className="label mb-1">Execution consistency</p>
            <p className="font-mono text-xs text-m-faint">
              Requires {CONFIG.MIN_REVIEWS_FOR_TREND} reviews. Current: {reviews.length}.
            </p>
          </div>
        )}

        {/* Category distribution (full_analysis) */}
        {has('full_analysis') && catTotal > 0 ? (
          <div className="card p-4">
            <p className="label mb-3">Category distribution</p>
            <div className="flex flex-col gap-2.5">
              {Object.entries(catMap)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-m-subtle w-32 shrink-0">{cat}</span>
                    <div className="flex-1 h-1 bg-m-muted overflow-hidden">
                      <div className="h-full bg-m-accent transition-all"
                        style={{ width: `${(count / catTotal) * 100}%` }} />
                    </div>
                    <span className="font-mono text-[10px] text-m-faint w-4 text-right">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        ) : !has('full_analysis') && (
          <div className="card p-4 opacity-40">
            <p className="label mb-1">Category distribution</p>
            <p className="font-mono text-xs text-m-faint">
              Unlocks at Day 30 with 3+ completed phases.
            </p>
          </div>
        )}

        {/* Effort distribution (full_analysis) */}
        {has('full_analysis') && (
          <div className="card p-4">
            <p className="label mb-3">Effort distribution</p>
            <div className="flex gap-3">
              {[[1, 'Low', 'text-m-subtle'], [2, 'Medium', 'text-m-warn'], [3, 'High', 'text-m-danger']].map(([v, l, cls]) => (
                <div key={v} className="flex-1 text-center">
                  <p className={`font-mono text-3xl ${cls}`}>{effortMap[v]}</p>
                  <p className="label mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Avg phase estimation per category (full_analysis) */}
        {has('full_analysis') && donePhases.length >= 3 && (() => {
          const phasesWithData = donePhases.filter(p => p.actual_weeks && p.estimated_weeks)
          if (!phasesWithData.length) return null
          const avg = phasesWithData.reduce((s, p) => s + (p.actual_weeks / p.estimated_weeks), 0) / phasesWithData.length
          return (
            <div className="card p-4">
              <p className="label mb-2">Phase estimation drift</p>
              <p className="font-mono text-sm text-m-text">
                Rolling average: <span className="text-m-accent font-mono">{(avg * 100).toFixed(0)}%</span> of estimated across {phasesWithData.length} phases.
              </p>
            </div>
          )
        })()}
      </div>
    </motion.div>
  )
}
